// Periodic snowfall over tall (massif-core) mountains. One Points system;
// each mountain cycles independently — a ~15s flurry every 80–150s
// (staggered by a per-mountain hash), flakes falling from just above the
// peak to its skirt with a light sway. Deterministic: phases come from a
// hash of the peak position, never Math.random.
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';

export interface SnowPeak {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

interface MountainSnowProps {
  readonly peaks: ReadonlyArray<SnowPeak>;
}

const FLAKES_PER_PEAK = 24;
const FALL_SECONDS = 15;
const FALL_HEIGHT = 1.6;
const FLAKE_SIZE = 0.09;

/** Deterministic per-peak hash from world position. */
function peakHash(x: number, z: number, salt: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233 + salt) * 43758.5453;
  return s - Math.floor(s);
}

export function MountainSnow({ peaks }: MountainSnowProps) {
  const pointsRef = useRef<THREE.Points>(null);

  const flakes = useMemo(() => {
    const total = peaks.length * FLAKES_PER_PEAK;
    const base = new Float32Array(total * 3); // peak anchor (kept for reset)
    const data: {
      peak: number;
      phase: number;
      period: number;
      ox: number;
      oz: number;
      sway: number;
    }[] = [];
    for (let m = 0; m < peaks.length; m++) {
      const peak = peaks[m]!;
      const period = 80 + peakHash(peak.x, peak.z, 1) * 70; // 80–150s
      for (let f = 0; f < FLAKES_PER_PEAK; f++) {
        base[(m * FLAKES_PER_PEAK + f) * 3] = peak.x;
        base[(m * FLAKES_PER_PEAK + f) * 3 + 1] = peak.y;
        base[(m * FLAKES_PER_PEAK + f) * 3 + 2] = peak.z;
        data.push({
          peak: m,
          phase: peakHash(peak.x + f * 0.37, peak.z - f * 0.61, 2) * period,
          period,
          ox: (peakHash(peak.x - f, peak.z + f, 3) * 2 - 1) * 0.55,
          oz: (peakHash(peak.x + f * 1.7, peak.z - f * 0.9, 4) * 2 - 1) * 0.55,
          sway: peakHash(peak.x * 1.3 + f, peak.z * 0.7, 5) * Math.PI * 2,
        });
      }
    }
    return { base, data, total };
  }, [peaks]);

  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(flakes.total * 3), 3));
    return g;
  }, [flakes]);

  const mat = useMemo(
    () =>
      new THREE.PointsMaterial({
        color: 0xf4f8ff,
        size: FLAKE_SIZE,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      }),
    [],
  );

  useEffect(
    () => () => {
      geo.dispose();
      mat.dispose();
    },
    [geo, mat],
  );

  useFrame(({ clock }) => {
    const pts = pointsRef.current;
    if (!pts || peaks.length === 0) return;
    const attr = pts.geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!attr) return;
    const pos = attr.array as Float32Array;
    const now = clock.elapsedTime;

    for (let i = 0; i < flakes.total; i++) {
      const d = flakes.data[i]!;
      const peak = peaks[d.peak]!;
      const t = (now + d.phase) % d.period;
      const idx = i * 3;
      if (t < FALL_SECONDS) {
        const p = t / FALL_SECONDS;
        pos[idx] = peak.x + d.ox + Math.sin(now * 1.3 + d.sway) * 0.12 * p;
        pos[idx + 1] = peak.y + 0.9 - p * FALL_HEIGHT;
        pos[idx + 2] = peak.z + d.oz + Math.cos(now * 1.1 + d.sway) * 0.12 * p;
      } else {
        // Parked below the world between flurries.
        pos[idx + 1] = -999;
      }
    }
    attr.needsUpdate = true;
  });

  if (peaks.length === 0) return null;
  return <points ref={pointsRef} geometry={geo} material={mat} frustumCulled={false} />;
}
